import { useState, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    Button,
    Grid,
    List,
    Alert,
    RadioGroup,
    Typography,
} from '@mui/material';

import ImageListItem from './ImageListItem';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const getImageKey = (image) => {
    const hasLocalFile = image?.file instanceof File || image?.file instanceof Blob;

    if (image?.savedToServer || !hasLocalFile) {
        return `server:${image?.newFileName || image?.originalFileName || image?.src || 'unknown'}`;
    }

    return `local:${image.file.name}:${image.file.size}:${image.file.lastModified}`;
};

const mergeUniqueImages = (currentImages, incomingImages) => {
    const existingKeys = new Set(currentImages.map(getImageKey));
    const uniqueImages = incomingImages.filter((image) => !existingKeys.has(getImageKey(image)));

    return currentImages.concat(uniqueImages);
};

const ImageUploader = ({ fieldName }) => {

    const { setValue } = useFormContext();

    const validFileTypes = ['jpg', 'jpeg', 'png'];
    const [uploadError, setUploadError] = useState('');

    const images = useWatch({
        name: fieldName,
        defaultValue: []
    });

    // Basically only used to update the RadioGroup
    const [bannerIndex, setBannerIndex] = useState(-1);

    const updateFilesWithBanner = (newBannerIndex) => {
        const updatedFiles = images.map((img, i) => ({
            ...img,
            bannerImage: newBannerIndex === i
        }));
        setValue(fieldName, updatedFiles);
    };

    // Run whenever the number of files changes. Using images.length
    // should be fine here.

    useEffect(() => {
        const oldBannerIndex = images.findIndex(img => img.bannerImage === true); // Returns -1 if not found.
        const newBannerIndex = oldBannerIndex < 0 ? 0 : oldBannerIndex; // If theres no banner image, default to the first image.

        setBannerIndex(newBannerIndex);
        updateFilesWithBanner(newBannerIndex);
    }, [images.length, setValue]);


    const handleFileChange = (event) => {
        setUploadError('');

        const newFiles = Array.from(event.target.files);
        const invalidTypeFiles = newFiles.filter(
            (file) => !validFileTypes.includes(file.name.split('.').pop().toLowerCase())
        );
        const oversizedFiles = newFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES);

        if (invalidTypeFiles.length || oversizedFiles.length) {
            const errorParts = [];

            if (invalidTypeFiles.length) {
                errorParts.push('Vain jpg-, jpeg- ja png-kuvat ovat sallittuja.');
            }

            if (oversizedFiles.length) {
                errorParts.push('Yksittäisen kuvan enimmäiskoko on 10 Mt.');
            }

            setUploadError(errorParts.join(' '));
        }

        const validFiles = newFiles
            .filter((file) => validFileTypes.includes(file.name.split('.').pop().toLowerCase()))
            .filter((file) => file.size <= MAX_FILE_SIZE_BYTES)
            .map(file => ({
                file,
                savedToServer: false
            }));

        const fileList = mergeUniqueImages(images, validFiles);
        setValue(fieldName, fileList);
        event.target.value = null;
    };

    const handleBannerChange = (event) => {
        const newBannerIndex = parseInt(event.target.value);
        setBannerIndex(newBannerIndex);
        updateFilesWithBanner(newBannerIndex);
    };

    // Always triggers the useEffect.
    const handleRemove = (indexToRemove) => {
        const updatedFiles = images.filter((file, index) => index !== indexToRemove);
        setValue(fieldName, updatedFiles);
    };

    let content;

    if (!images?.length)
        content = (
            <Alert severity='info' sx={{ borderRadius: 3 }}>
                Tapahtumalla ei vielä ole kuvia
            </Alert>
        );
    else
        content = images?.map((image, i) => (
            <ImageListItem
                key={`${image?.file?.name ?? image.originalFileName}-${i}`}
                image={image}
                value={i}
                isBanner={i === bannerIndex}
                handleChange={handleBannerChange}
                handleRemove={() => handleRemove(i)} // Index removal is safe here.
            />
        ));

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                {uploadError && (
                    <Alert severity='error' sx={{ mb: 2, borderRadius: 3 }} onClose={() => setUploadError('')}>
                        {uploadError}
                    </Alert>
                )}
                <RadioGroup value={bannerIndex} onChange={handleBannerChange}>
                    <List disablePadding>
                        {content}
                    </List>
                </RadioGroup>
            </Grid>
            <Grid item xs={12} justifyContent='space-between' display='flex'>
                <Typography align='center' variant='caption' sx={{ pr: 3, display: 'flex', alignItems: 'flex-end' }}>
                    Kuvien tulee olla jpg- tai png-tiedostoja. Enimmäiskoko 10 Mt ja suositeltu pikselikoko 1200 x 628 px.
                </Typography>
                <Button
                    component='label'
                    variant='contained'
                    size='small'
                    sx={{ backgroundColor: 'taphaBlue.main' }}
                    startIcon={ <UploadFileIcon sx={{ color: 'white' }} /> }>

                    <input
                        type='file'
                        multiple
                        hidden
                        accept='.png, .jpg, .jpeg'
                        name={fieldName}
                        onChange={handleFileChange}
                    />
                    Lisää kuva
                </Button>
            </Grid>
        </Grid>
    );
};

export default ImageUploader;
